import React from "react";
import {
  ArrowRightLeft,
  Grid3x3,
  Loader2,
  MicOff,
  PauseCircle,
  PhoneCall,
  PhoneOff,
  Users,
  Voicemail,
} from "lucide-react";

/**
 * AW-VIS-2 -- the original bottom call-control cluster, as shared,
 * fixture-free presentation (WORKSPACE-FEATURE-001 S15; authoritative
 * original `WorkspaceCallControls` at commit 23d500e27; reference
 * `docs/design/prototypes/workspace-v1-reference.png`).
 *
 * WHY THIS WAS EXTRACTED RATHER THAN DUPLICATED
 * ---------------------------------------------
 * The circular control row is the single most recognisable element of
 * the authoritative design, and until now it existed only inside the
 * development-only `WorkspaceCallControls`, which imports
 * `workspaceFixtures` for its key enum. Production could not reuse it
 * without dragging a fixture module across the firewall, so PR-A built
 * a text-only bar instead and the original strip never reached an
 * agent's screen.
 *
 * The fix is the same one PR-A-VIS-1 applied to `WorkspaceMainCard`:
 * remove the coupling instead of cloning the component. The key enum is
 * declared here (it is presentation vocabulary, not fixture data), every
 * control is injected by its caller, and this file imports nothing but
 * React and icons. The development shell and Production now render the
 * same cluster from the same source.
 *
 * SCAFFOLDING IS NOT CAPABILITY
 * -----------------------------
 * A control may be present and unavailable. That is a deliberate
 * product decision for this restoration: Spencer evaluates the complete
 * original workspace while seeing exactly which capabilities remain to
 * be built. The safety rule is carried by the component itself rather
 * than left to each caller -- an unavailable control renders as a real
 * `disabled` button with `aria-disabled`, `tabIndex={-1}` and NO
 * `onClick` bound at all, so it cannot fire a handler even if a caller
 * passes one by mistake. It fabricates nothing: no duration, no
 * recording state, no call identity.
 *
 * That is the #685 lesson applied in the one direction that is still
 * safe. #685 was a control that *looked* operable and did nothing. A
 * control that is visibly, programmatically and semantically
 * unavailable makes the opposite promise, and keeps it.
 */

export const BOTTOM_CONTROL = Object.freeze({
  HOLD: "hold",
  MUTE: "mute",
  KEYPAD: "keypad",
  VOICEMAIL: "voicemail",
  HANG_UP: "hang-up",
  TRANSFER: "transfer",
  CONFERENCE: "conference",
  ADD_CALL: "add-call",
});

/**
 * The authoritative control order, icons and labels, taken from the
 * original `WorkspaceCallControls` and the committed reference mockup.
 * Order is stable and is itself part of the design contract -- Hang Up
 * sits fifth, centred in the row, which is why it reads as the
 * consequential action without needing to shout (S15.7).
 */
export const BOTTOM_CONTROL_CATALOG = Object.freeze([
  { key: BOTTOM_CONTROL.HOLD, label: "Hold", Icon: PauseCircle },
  { key: BOTTOM_CONTROL.MUTE, label: "Mute", Icon: MicOff },
  { key: BOTTOM_CONTROL.KEYPAD, label: "Keypad", Icon: Grid3x3 },
  { key: BOTTOM_CONTROL.VOICEMAIL, label: "Voicemail", Icon: Voicemail },
  { key: BOTTOM_CONTROL.HANG_UP, label: "Hang Up", Icon: PhoneOff, emphasis: "critical" },
  { key: BOTTOM_CONTROL.TRANSFER, label: "Transfer", Icon: ArrowRightLeft },
  { key: BOTTOM_CONTROL.CONFERENCE, label: "Conference", Icon: Users },
  { key: BOTTOM_CONTROL.ADD_CALL, label: "Add Call", Icon: PhoneCall },
]);

const BASE_BUTTON =
  "flex h-11 w-11 items-center justify-center rounded-full border transition-colors";

function toneClass({ emphasis, available }) {
  if (!available) {
    return emphasis === "critical"
      ? "cursor-not-allowed border-red-500/20 bg-red-500/[0.06] text-red-300/40"
      : "cursor-not-allowed border-white/10 bg-white/[0.03] text-slate-300/40";
  }
  return emphasis === "critical"
    ? "border-red-500/40 bg-red-500/20 text-red-200 hover:bg-red-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
    : "border-white/15 bg-white/[0.06] text-slate-100 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400";
}

/**
 * One circular control. `available` is the only thing that decides
 * whether a handler is bound -- the caller cannot accidentally make an
 * unavailable control live.
 */
export function CircleControlButton({
  label,
  Icon,
  emphasis,
  available = false,
  pending = false,
  onActivate,
  reasonId,
  testId,
}) {
  const isDisabled = !available || pending;
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-label={label}
        aria-describedby={available ? undefined : reasonId}
        tabIndex={isDisabled ? -1 : undefined}
        onClick={isDisabled ? undefined : onActivate}
        data-testid={testId}
        data-available={String(Boolean(available))}
        className={`${BASE_BUTTON} ${toneClass({ emphasis, available: available && !pending })}`}
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Icon className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      <span className={available ? "text-[11px] text-slate-300" : "text-[11px] text-slate-500"}>
        {label}
      </span>
    </div>
  );
}

/**
 * The control row itself.
 *
 * `controls` is a map of control key -> { available, pending,
 * onActivate, testId }. Any key absent from the map renders in its
 * authoritative position as unavailable. That default is deliberate:
 * adding a capability must be an explicit act, and forgetting to wire
 * one can only ever produce a dead-but-honest control, never a live one.
 */
export default function WorkspaceCallControlCluster({ controls = {}, reasonId }) {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-2.5"
      role="group"
      aria-label="Call controls"
      data-testid="workspace-call-control-cluster"
    >
      {BOTTOM_CONTROL_CATALOG.map((control) => {
        const wiring = controls[control.key] || {};
        return (
          <CircleControlButton
            key={control.key}
            label={control.label}
            Icon={control.Icon}
            emphasis={control.emphasis}
            available={Boolean(wiring.available)}
            pending={Boolean(wiring.pending)}
            onActivate={wiring.onActivate}
            reasonId={reasonId}
            testId={wiring.testId || `workspace-control-${control.key}`}
          />
        );
      })}
    </div>
  );
}
